import React from "react";
import { API } from "@/lib/api";
import { WhatsAppIcon, waLink } from "@/components/WhatsAppButton";
import { ImageIcon, Flame } from "lucide-react";
import { useMemberMessage } from "@/lib/waTemplates";
import { ProductCard, CardTag, cardBtnClass } from "@/components/ProductCard";

const CLIENT_KEY = "zola_client_id";

function clientId() {
  let id = localStorage.getItem(CLIENT_KEY);
  if (!id) {
    id = (crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`).replace(/[^\w-]/g, "");
    localStorage.setItem(CLIENT_KEY, id);
  }
  return id;
}

function trackEnquiry(offerId, source) {
  try {
    fetch(`${API}/offers/${offerId}/enquiry`, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ source, client_id: clientId() }),
    }).catch(() => {});
  } catch {
    /* tracking must never block WhatsApp */
  }
}

export const formatPrice = (o) => o.price
  ? `${o.price_currency || "AED"} ${Number(o.price).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
  : null;

export function enquiryMessage(title, memberId) {
  return memberId
    ? `Hi Zola, I'm interested in this offer: ${title}. My membership ID is ${memberId}.`
    : `Hi Zola, I'm interested in this offer: ${title}. Could you share more details?`;
}

export function OfferThumb({ src, className = "" }) {
  return (
    <div className={`aspect-square overflow-hidden rounded-xl ${className}`} style={{ background: "var(--bg-soft)" }}>
      {src ? (
        <img src={`${API.replace(/\/api$/, "")}${src}`} alt="" loading="lazy" decoding="async"
             className="h-full w-full object-cover" />
      ) : (
        <div className="h-full w-full grid place-items-center" style={{ color: "var(--text-muted)" }}
             data-testid="offer-image-placeholder">
          <ImageIcon className="h-6 w-6 opacity-60" />
        </div>
      )}
    </div>
  );
}

export default function OfferCard({ offer: o, source, waNumber, memberId, spotlight = false }) {
  const memberMsg = useMemberMessage("offer");
  const portal = source === "customer_portal";
  const msg = portal && memberMsg
    ? memberMsg({ title: o.title, partner: o.partner_name || (o.source === "Partner" ? "Zola Partner" : o.source), price: formatPrice(o) })
    : enquiryMessage(o.title, portal ? memberId : null);
  const href = waNumber ? waLink(waNumber, msg) : null;
  const enquire = () => {
    if (!href) return;
    window.open(href, "_blank", "noopener,noreferrer");
    trackEnquiry(o.id, source);
  };
  const onKey = (e) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); enquire(); }
  };

  const clickable = {
    onClick: enquire,
    onKeyDown: href ? onKey : undefined,
    role: href ? "link" : undefined,
    tabIndex: href ? 0 : undefined,
    "aria-label": href ? `Enquire about ${o.title} on WhatsApp` : undefined,
  };
  const price = formatPrice(o);

  return (
    <ProductCard
      {...clickable}
      className={href ? "cursor-pointer" : ""}
      image={o.image_url ? `${API.replace(/\/api$/, "")}${o.image_url}` : null}
      title={o.title}
      description={o.description}
      meta={o.expiry_date ? `Ends ${new Date(o.expiry_date).toLocaleDateString()}` : null}
      price={price}
      priceTestid={`offer-price-${o.id}`}
      highlight={spotlight}
      testid={spotlight ? "offer-spotlight" : `offer-${o.id}`}
      tagLeft={o.source && <CardTag gold={o.source !== "Zola"}>{o.partner_name || o.source}</CardTag>}
      tagRight={o.discount && <CardTag gold testid={`offer-discount-${o.id}`}>{o.discount}</CardTag>}
      badge={spotlight && <div className="mb-1.5"><CardTag gold testid="offer-spotlight-badge"><Flame className="h-3 w-3" /> Most enquired</CardTag></div>}
      action={href && (
        <button type="button" onClick={(e) => { e.stopPropagation(); enquire(); }} className={cardBtnClass}
                data-testid={`offer-enquire-btn-${o.id}`}>
          <WhatsAppIcon className="flex-shrink-0 h-4 w-4 text-[#25D366]" /> Enquire Now
        </button>
      )}
    />
  );
}
